import { Injectable } from '@angular/core';
import { Api } from '../shared/api';
import { Globals } from '../shared/globals';
import {
  CardTerminal,
  ChargingPointNumber,
  ChargingPointNumbering,
  RegisterCardTerminalRequest,
  TerminalAssignment,
} from '../models/card-terminal.model';

/**
 * Card terminals (Stripe Terminal readers) of the signed-in station owner.
 * Every call answers 404 while the card terminal feature is not enabled for the user.
 */
@Injectable()
export class CardTerminalService {
  private readonly url: string;

  constructor(private api: Api, globals: Globals) {
    this.url = globals.apiUrl('terminals');
  }

  getTerminals(): Promise<CardTerminal[]> {
    return this.api.httpGet<CardTerminal[]>(this.url);
  }

  registerTerminal(
    request: RegisterCardTerminalRequest
  ): Promise<CardTerminal> {
    return this.api.httpPost<RegisterCardTerminalRequest, CardTerminal>(
      this.url,
      request
    );
  }

  /** Assigns the station group the terminal offers, or clears it with null. */
  assignStationGroup(
    terminalId: string,
    stationGroupId: string | null
  ): Promise<TerminalAssignment> {
    return this.api.httpPut<
      { stationGroupId: string | null },
      TerminalAssignment
    >(`${this.url}/${terminalId}/station-group`, { stationGroupId });
  }

  /** Assigns a single station the terminal offers, or clears it with null. */
  assignStation(
    terminalId: string,
    stationId: string | null
  ): Promise<TerminalAssignment> {
    return this.api.httpPut<{ stationId: string | null }, TerminalAssignment>(
      `${this.url}/${terminalId}/station`,
      { stationId }
    );
  }

  /** The charging points the terminal offers, with the number each has at this terminal. */
  getChargingPoints(terminalId: string): Promise<ChargingPointNumbering> {
    return this.api.httpGet<ChargingPointNumbering>(
      `${this.url}/${terminalId}/charging-points`
    );
  }

  /** Replaces all numbers of the terminal; charging points left out have none afterwards. */
  setChargingPointNumbers(
    terminalId: string,
    numbers: ChargingPointNumber[]
  ): Promise<ChargingPointNumbering> {
    return this.api.httpPut<
      { numbers: ChargingPointNumber[] },
      ChargingPointNumbering
    >(`${this.url}/${terminalId}/charging-point-numbers`, { numbers });
  }

  /**
   * The PIN of the terminal's admin menu, where a new pairing code is made. A terminal registered before
   * terminals had PINs of their own gets one with this call.
   */
  async getAdminPasscode(terminalId: string): Promise<string> {
    const result = await this.api.httpGet<{ Passcode: string }>(
      `${this.url}/${terminalId}/admin-passcode`
    );
    return result.Passcode;
  }

  removeTerminal(terminalId: string): Promise<void> {
    return this.api.httpDelete(`${this.url}/${terminalId}`);
  }
}
